import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { IoMdAdd } from "react-icons/io";
import { format } from "date-fns";

import { DynamicTable } from "@/components";
import {
  setDownloadProgress,
  stopDownloadPolling,
  useDeleteReportsMutation,
  useGetAllReportsQuery,
  useLazyGetDownloadStatusQuery,
} from "@/services";
import { useAppDispatch, useAppSelector } from "@/store";
import { useSnackbar } from "@/hooks";
import { roles } from "@/roles";
import { reportColumns } from "@/columns";
import { Fiters } from "./components";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import ProcessBox from "../trade-data/ProcessBox";
import { getFilenameFromUrl } from "@/utils";
import { EnhancedReconciliationModal } from "@/components";

let interval: NodeJS.Timeout | null = null;

const PostImportAdjustment: FC = (): ReactElement => {
  const { show } = useSnackbar();
  const dispatch = useAppDispatch();
  const {
    isDownloadPollingActive,
    downloadFileId,
    downloadProgressValue,
    activeClientId,
  } = useAppSelector((state) => state.polling);
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [filters, setFilters] = useState({
    adjustmentType: "" as "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty",
    startDate: new Date("1 Jan 2000"),
    endDate: new Date(),
  });
  const [ids, setIds] = useState<{ delete: string[]; download: string[] }>({
    delete: [],
    download: [],
  });
  const [hasDownloadedFile, setHasDownloadedFile] = useState<boolean>(false);
  const [showEnhancedModal, setShowEnhancedModal] = useState<boolean>(false);
  const progressSubscriberRef = useRef<
    ((options: SubscriptionOptions) => void) | null
  >(null);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false; // Allow query to run for TDADMIN with TradeDive Client, even if selectedClient is not set
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, user]
  );

  const paramFilters = useMemo(() => {
    return {
      pageNo: page,
      pageSize: limit,
      startDate: format(filters.startDate ?? new Date(), "yyyy-MM-dd"),
      endDate: format(filters.endDate ?? new Date(), "yyyy-MM-dd"),
      ...(filters.adjustmentType && { adjustmentType: filters.adjustmentType }),
      clientId: selectedClient?._id,
      ...(isTDAdmin && { tenantId: selectedTenant?._id }),
      onlyAdjustments: true,
    };
  }, [page, limit, filters, selectedClient, selectedTenant]);

  const { data: apiData, isFetching, refetch } = useGetAllReportsQuery(
    { ...paramFilters },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const [
    fetchDownloadProgress,
    { data: downloadProgress, error: downloadError },
  ] = useLazyGetDownloadStatusQuery({
    pollingInterval: 5000,
  });

  const [deleteReport] = useDeleteReportsMutation();

  const { data: reports = [], meta } = apiData ?? {};
  const totalReports = meta?.countDocs ?? 0;

  const hasCreateAccess = useMemo(() => {
    return (
      !!user &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDCLIENT, roles.TDADMIN].includes(user.userRole)
    );
  }, [user]);

  const handleEnhancedReconciliation = () => {
    setShowEnhancedModal(true);
  };

  const deleteReportAction = async (report: IReport) => {
    const { _id } = report;

    setIds({ delete: [_id], download: [...ids.download] });
    const isDeleted = await deleteReport(_id).unwrap();
    setIds({ delete: [], download: [...ids.download] });

    if (isDeleted) {
      show(
        "Successfully deleted!",
        `Report with id ${_id} deleted sucessfully!`,
        "success"
      );
    }
  };

  const pullProgress = useCallback(
    async (fileId: string) => {
      const progressSubscriber = fetchDownloadProgress(fileId);

      progressSubscriberRef.current =
        progressSubscriber.updateSubscriptionOptions;
    },
    [fetchDownloadProgress]
  );

  // Restart polling if exited by page changing
  useEffect(() => {
    if (isDownloadPollingActive && downloadFileId) {
      pullProgress(downloadFileId);
    }
  }, [isDownloadPollingActive, downloadFileId, pullProgress]);

  useEffect(() => {
    if (
      progressSubscriberRef.current &&
      (downloadError ||
        downloadProgress?.status === "SUCCESS" ||
        downloadProgress?.status === "ERROR")
    ) {
      progressSubscriberRef.current({ pollingInterval: 0 });
      progressSubscriberRef.current = null;
      dispatch(stopDownloadPolling());
    }
  }, [downloadProgress, downloadError, dispatch]);

  useEffect(() => {
    if (interval) {
      clearInterval(interval);
    }

    if (
      downloadError ||
      downloadProgress?.status === "SUCCESS" ||
      downloadProgress?.status === "FAILED"
    ) {
      dispatch(setDownloadProgress(100));
      return;
    }

    if (downloadProgressValue >= 90) {
      return;
    }

    if (downloadProgress?.status === "PENDING" && downloadProgressValue < 90) {
      interval = setInterval(() => {
        const randomIncrement = Math.floor(Math.random() * 6) + 5; // Random number between 5 and 10

        dispatch(
          setDownloadProgress(
            Math.min(downloadProgressValue + randomIncrement, 100)
          )
        ); // Ensure progress doesn't exceed 100
      }, 20000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [downloadProgressValue, downloadProgress, downloadError, dispatch]);

  const downloadFile = async (signedUrl: string) => {
    try {
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error("Download failed!");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = getFilenameFromUrl(signedUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      show("Successfully downloaded", ``, "success");
      setHasDownloadedFile(true);
    } catch (error) {
      if (error instanceof Error) {
        show("Failed to download file", error.message, "error");
      }
    }
  };

  // Legacy download action - only used for non-report downloads now
  const downloadFileAction = async (_report: IReport) => {
    // This is now handled by the reportColumns component directly
    // Keeping this for backward compatibility with other download flows
    console.warn('downloadFileAction called - this should be handled by reportColumns now');
  };

  const actions = {
    delete: deleteReportAction,
    download: downloadFileAction,
  };

  const handleActions = (type: "download" | "delete", report: IReport) => {
    actions[type](report);
  };

  return (
    <Stack flex={1} p={0.5}>
      <Grid container sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography color="#020202" fontSize={24} fontWeight={500}>
            Post Import Adjustment{" "}
            <Typography
              component="span"
              color="#AEB3B3"
              fontSize={24}
              fontWeight={400}
            >
              ({totalReports})
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              justifyContent: {
                xs: "center",
                sm: "flex-end",
              },
              marginTop: {
                xs: 2,
                sm: 0,
              },
            }}
          >
            <Fiters onFiltersChange={(filterUpdate) => {
              setFilters({
                ...filters,
                adjustmentType: filterUpdate.adjustmentType,
                startDate: filterUpdate.startDate,
                endDate: filterUpdate.endDate,
              });
            }} />
            {hasCreateAccess && (
              <Button
                variant="primary-contained"
                startIcon={<IoMdAdd />}
                sx={{ fontSize: "16px", fontWeight: 500 }}
                onClick={handleEnhancedReconciliation}
              >
                Create Adjustment
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
      {((activeClientId === selectedClient?._id &&
        downloadProgress?.status === "PENDING") ||
        downloadProgress?.msg) &&
        !hasDownloadedFile && (
          <ProcessBox
            title={downloadProgress.msg}
            progress={downloadProgressValue}
            {...(downloadProgress?.url && {
              hideLoader: true,
              renderSubtitle: () => (
                <span>
                  Click here to download the{" "}
                  <Box
                    component="b"
                    onClick={() => downloadFile(downloadProgress?.url ?? "")}
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    file
                  </Box>
                </span>
              ),
            })}
          />
        )}

      <DynamicTable
        data={reports}
        stickyHeader
        hideScrollBars={false}
        isLoading={isFetching || shouldSkip}
        columns={reportColumns({
          action: handleActions,
          actionsLoading: {
            ...ids,
          },
        })}
        sx={{
          flex: 1,
          "& .MuiTableContainer-root": { '&::-webkit-scrollbar': { height: 6, width: 6 } },
          "& .MuiTable-root": {
            borderCollapse: "collapse",
            tableLayout: 'fixed',
            width: '100%',
            minWidth: { xs: 800, md: '100%' },
            "& .MuiTableCell-root": {
              fontSize: { xs: '11px', md: '12px' },
              padding: { xs: '6px 8px', md: '8px 10px' },
            },
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
        handlePageChange={setPage}
        handleLimitChange={setLimit}
        limit={limit}
        page={page}
        total={totalReports}
        hidePagination={false}
      />
      
      <EnhancedReconciliationModal
        show={showEnhancedModal}
        setShow={setShowEnhancedModal}
        onClose={() => {
          // Refetch reports data when modal is closed
          refetch();
        }}
      />
    </Stack>
  );
};

export default PostImportAdjustment;
